import { consumesReaction, inCombatGate, passesDbGate, teamGate } from '../reactions/trigger-dispatch';
import { statusPreventsActions } from '../actions/status-action-gate';
import { readItemField, type ItemFieldRow } from '../compile/item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { equippedWeaponClassificationMatches } from '../compile/equipped-weapon-property';
import { heldInventory } from '../inventory/held-inventory';
import { THROW_HAND_SLOTS } from '../actions/throw-object-gate';
import { encounterCreatureField } from '../../shared/array-types';
import type { PlainWalkReaders } from './plain-walk-scope';

type Row = Record<string, unknown>;

/** A false answer proves this particular definition cannot fire at this mover.
 * Missing equipment/definitions remain possible, so their existing named refusal survives.
 * The native dispatcher owns combat/team/reaction semantics; this gate never executes an effect. */
export function authoredWatcherMayReact(encounter: Row, watcher: Row, mover: Row, definition: Row, read: PlainWalkReaders): boolean {
  const native = { value: (row: Row | null, key: string) => row ? read.value(row, key) : null,
    entries: (row: Row | null, key: string) => row ? read.entries(row, key) : [], itemField: readItemField,
    inCombat: (row: Row) => read.inCombat(encounter, row), opposing: read.opposingTeams };
  if (!inCombatGate(native, definition, watcher) || !teamGate(native, definition, watcher, mover)) return false;
  if (consumesReaction(native, definition) && (statusPreventsActions(watcher, read, 'no_reactions')
    || read.inCombat(encounter, watcher) && Number(encounterCreatureField(watcher.encounter_creatures, 'reaction') ?? 0) <= 0)) return false;
  if (!passesDbGate(native, String(readItemField(definition, 'item_id') ?? ''), watcher,
    String(encounterCreatureField(mover.encounter_creatures, 'encounter_creature_id') ?? ''))) return false;

  const key = 'if_weapon_classification_equipped_active';
  if (read.value(definition, key) == null && !read.entries(definition, key).length) return true;
  const inventory = heldInventory(watcher, native.value);
  if (inventory === null) return true;
  const classifications = read.entries(definition, key).map(readLayerListEntryUuid);
  let unknown = false;
  for (const entry of inventory) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) { unknown = true; continue; }
    const row = entry as Row;
    const value = (subject: ItemFieldRow, name: string): unknown => {
      const held = read.value(subject as Row, name);
      if (held != null) return held;
      const id = String(readInventoryField(subject, 'inventory_item_id') ?? '');
      const def = id ? read.definition(id) : null;
      if (id && !def) unknown = true;
      return def ? read.value(def, name) : null;
    };
    if (classifications.some(id => equippedWeaponClassificationMatches(row, id,
      { layerValue: value, layerEntries: (subject, name) => read.entries(subject as Row, name) }, THROW_HAND_SLOTS))) return true;
  }
  return unknown;
}

/** Bind only readers, never clone or mutate encounter/member state. All reach proofs share this gate. */
export function authoredWatcherReaders(encounter: Row, mover: Row, read: PlainWalkReaders): PlainWalkReaders {
  return { ...read, watcherMayReact: (watcher, definition) => authoredWatcherMayReact(encounter, watcher, mover, definition, read) };
}
